import { useCallback } from 'react'
import { buildOpenBugsMarkdown } from '../../lib/bug-management/copyReports'
import { downloadTextFile, sanitizeFilename } from '../../lib/downloadFile'
import { useLoadAllProjectBugs, type LoadAllProjectBugsFilters } from './useLoadAllProjectBugs'
import { useGetProjectQuery } from '../../services/projectDataApi'

// Gather every open bug for a project and save it as one markdown file named "<project name>_all-bugs.md".
// Extracted from DownloadBugReportsButton so the exact same export can be fired WITHOUT the button —
// notably the run-finished notice's "download all bug reports" link, which deep-links into the Bugs tab
// with `?download=all` and triggers this on mount (see BugListTab). Returns whether the file was produced
// so callers can surface success/failure the way the button does.
export function useDownloadBugReports(
  projectId: string,
): (filters?: Omit<LoadAllProjectBugsFilters, 'projectId'>) => Promise<boolean> {
  const loadAllBugs = useLoadAllProjectBugs()
  // Cached by RTK Query — the overview/bug list already fetched the project, so this is free here.
  const projectName = useGetProjectQuery(projectId).data?.name
  return useCallback(
    async (filters) => {
      try {
        const allBugs = await loadAllBugs({ projectId, ...filters })
        const markdown = buildOpenBugsMarkdown(allBugs)
        const base = sanitizeFilename(projectName ?? 'project', 'project')
        downloadTextFile(`${base}_all-bugs.md`, markdown)
        return true
      } catch {
        return false
      }
    },
    [loadAllBugs, projectId, projectName],
  )
}
